<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useAuthStore } from "@/stores/auth";
import { useConfigStore } from "@/stores/config";

const { t } = useI18n();
const auth = useAuthStore();
const configStore = useConfigStore();

interface Section {
  name: string;
  to: string;
  key: string;
  ownerOnly?: boolean;
  selfHostedOnly?: boolean;
}

const SECTIONS: Section[] = [
  { name: "organizations", to: "/admin/organizations", key: "admin.nav.organizations" },
  { name: "operators", to: "/admin/operators", key: "admin.nav.operators" },
  { name: "agents", to: "/admin/agents", key: "admin.nav.agents" },
  {
    name: "nx-endpoints",
    to: "/admin/nx-endpoints",
    key: "admin.nav.nx_endpoints",
    selfHostedOnly: true,
  },
  { name: "releases", to: "/admin/releases", key: "admin.nav.releases" },
  { name: "audit", to: "/admin/audit", key: "admin.nav.audit" },
  { name: "beta-requests", to: "/admin/beta-requests", key: "admin.nav.beta" },
];

const sections = computed(() => {
  const mode = configStore.state.kind === "ready" ? configStore.state.config.mode : "self-hosted";
  return SECTIONS.filter((s) => {
    if (s.ownerOnly && !auth.hasRole(["owner"])) return false;
    if (s.selfHostedOnly && mode !== "self-hosted") return false;
    return true;
  });
});
</script>

<template>
  <div class="admin">
    <h1 class="admin__title">{{ t("nav.admin") }}</h1>
    <div class="admin__body">
      <!-- Persistent section nav: a rail on desktop, scrollable tabs on mobile. -->
      <nav class="admin__rail" :aria-label="t('a11y.admin_sections')">
        <RouterLink v-for="s in sections" :key="s.name" :to="s.to" class="admin__rail-link">
          {{ t(s.key) }}
        </RouterLink>
      </nav>
      <section class="admin__content">
        <RouterView />
      </section>
    </div>
  </div>
</template>

<style scoped>
.admin {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.admin__title {
  margin: 0;
  font-size: 1.2rem;
}
.admin__body {
  display: grid;
  grid-template-columns: 13rem 1fr;
  gap: 1.25rem;
  align-items: start;
}
.admin__rail {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  position: sticky;
  top: 4.5rem;
}
.admin__rail-link {
  display: block;
  padding: 0.55rem 0.75rem;
  border-radius: 6px;
  color: var(--color-fg);
  border: 1px solid transparent;
  min-height: 40px;
  line-height: 1.7;
}
.admin__rail-link:hover {
  border-color: var(--color-border);
  text-decoration: none;
}
.admin__rail-link.router-link-active {
  background: var(--color-bg-elev);
  border-color: var(--color-accent);
  color: var(--color-accent);
  font-weight: 600;
}
.admin__content {
  min-width: 0;
}

@media (max-width: 760px) {
  .admin__body {
    grid-template-columns: 1fr;
    gap: 0.85rem;
  }
  /* Horizontal scrollable tabs above the content. */
  .admin__rail {
    position: static;
    flex-direction: row;
    gap: 0.4rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 0.25rem;
    border-bottom: 1px solid var(--color-border);
  }
  .admin__rail::-webkit-scrollbar {
    display: none;
  }
  .admin__rail-link {
    flex: 0 0 auto;
    white-space: nowrap;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
}
</style>
